'use client'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, ScanLine } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import {
  getCarePackagesUrl,
  getDiseaseCategoriesProductListUrl,
} from '@/utils/commonFunc'
import { useSelector } from '@/lib/store'
import { DEFAULT_CITY_SLUG } from '@/utils/constants'

interface ExploreByHealthNeedProps {
  ssrData?: any[] | null
}

const ExploreByHealthNeed = ({ ssrData }: ExploreByHealthNeedProps) => {
  const [isHydrated, setIsHydrated] = useState(false)
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const cityState = useSelector((state) => state.city)
  // The persisted city only reaches Redux after mount, so this stays on its SSR value
  // until hydration completes.
  const citySlug =
    (isHydrated ? cityState?.currentCityObj?.slugName : DEFAULT_CITY_SLUG) ||
    DEFAULT_CITY_SLUG
  // Filter before slicing so a malformed entry does not consume one of the
  // eight visible tiles. The name is required: it is the React key, the image
  // alt, and the source of the link slug.
  const diseaseCategoryData = (Array.isArray(ssrData) ? ssrData : [])
    .filter((item: any) => item?.diseaseCategoryName)
    .slice(0, 8)
  const categoriesUrl = `/disease/${citySlug}/categories`

  return (
    <section className="container max-w-full pt-6 md:pt-12 pb-6 md:pb-12 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <h3 className="text-heading-md md:text-heading-lg text-gray-900 mb-1 sm:mb-2">
          Explore by Health Need
        </h3>
        <Link
          href={categoriesUrl}
          className="hidden md:flex items-center gap-2 font-semibold text-label-md tracking-wide bg-white px-4 py-2 rounded-md text-primary-600 border border-primary-100"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Mobile: 2-column grid */}
      <div className="md:hidden">
        <div className="grid grid-cols-2 gap-2.5">
          {/* Featured: Full Body Checkups — static CTA, spans full width */}
          <Link
            href={getCarePackagesUrl(citySlug)}
            className="col-span-2 block focus:outline-none"
          >
            <div className="flex items-center gap-2.5 bg-white border border-gray-300 rounded-lg px-3 py-2.5 cursor-pointer hover:border-primary-300 transition-colors duration-200">
              <span className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <ScanLine className="w-5 h-5 text-primary-600" />
              </span>
              <span className="text-label-sm font-semibold text-gray-800 leading-tight">
                Full Body Checkups
              </span>
              <span className="ml-auto px-2 py-0.5 text-label-xs font-bold text-primary-600 bg-primary-50 rounded-full tracking-wider uppercase flex-shrink-0">
                Popular
              </span>
            </div>
          </Link>

          {/* API-driven disease category cards */}
          {diseaseCategoryData.map((item: any) => (
            <Link
              key={item.diseaseCategoryName}
              href={getDiseaseCategoriesProductListUrl(
                citySlug,
                item.diseaseCategoryName
              )}
              className="block focus:outline-none"
            >
              <div className="flex items-center gap-2.5 bg-white border border-gray-300 rounded-lg px-3 py-2.5 cursor-pointer hover:border-primary-300 transition-colors duration-200">
                <span className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  {item.imageUrl && (
                    <Image
                      src={item.imageUrl}
                      alt={item.diseaseCategoryName}
                      width={24}
                      height={24}
                      className="object-contain"
                    />
                  )}
                </span>
                <span className="text-label-sm font-semibold text-gray-800 leading-tight line-clamp-2">
                  {item.diseaseCategoryName}
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile CTA */}
        <Link
          href={categoriesUrl}
          className="mt-4 w-full flex items-center justify-center gap-2 font-semibold text-label-md tracking-wide uppercase bg-white py-3 rounded-md border border-primary-100 text-primary-600 hover:text-primary-700 transition-colors duration-200"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Desktop: grid layout */}
      <div className="hidden md:grid md:grid-cols-6 gap-3">
        {/* Featured: Full Body Checkups — static CTA, spans 2 cols x 2 rows */}
        <div className="col-span-2 row-span-2">
          <Link
            href={getCarePackagesUrl(citySlug)}
            className="text-left w-full h-full block"
          >
            <div className="group bg-white border border-gray-300 rounded-lg h-full min-h-0 flex flex-col p-5 cursor-pointer transition-all duration-300 hover:shadow-md">
              <div>
                <span className="inline-block px-3 py-1 text-label-xs font-bold text-primary-600 bg-primary-50 rounded-full tracking-wider uppercase mb-3">
                  Most Popular
                </span>
                <h4 className="text-heading-md font-bold text-gray-800 leading-tight mb-1 tracking-tight">
                  Full Body Checkups
                </h4>
                <p className="text-body-sm text-gray-700 max-w-[280px] leading-relaxed">
                  Comprehensive screening covering over 90+ parameters for a
                  holistic health view.
                </p>
              </div>
              <div className="mt-auto">
                {/* A span, not Button: this whole card is already the Link's <a>, and a <button> can't nest inside one. */}
                <span
                  className={buttonVariants({
                    variant: 'agilus_primary',
                    size: 'default',
                    className: 'gap-2',
                  })}
                >
                  Explore Packages
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* API-driven disease category cards */}
        {diseaseCategoryData.map((item: any) => (
          <div key={item.diseaseCategoryName}>
            <Link
              href={getDiseaseCategoriesProductListUrl(
                citySlug,
                item.diseaseCategoryName
              )}
              className="block h-full"
            >
              <div className="group flex flex-col justify-between bg-white rounded-lg p-3 md:p-4 h-full min-h-[100px] md:min-h-[110px] transition-all duration-300 cursor-pointer border border-gray-300">
                <div className="w-9 h-9 bg-primary-50 group-hover:bg-primary-100 rounded-lg flex items-center justify-center transition-colors duration-300">
                  {item.imageUrl && (
                    <Image
                      src={item.imageUrl}
                      alt={item.diseaseCategoryName}
                      width={28}
                      height={28}
                      className="object-contain"
                    />
                  )}
                </div>
                <p className="text-label-sm md:text-label-md font-semibold text-gray-800 group-hover:text-primary-700 transition-colors duration-300 leading-tight mt-auto">
                  {item.diseaseCategoryName}
                </p>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </section>
  )
}

export default ExploreByHealthNeed
